import { useState, useEffect } from "react";
import { useAuthStore } from "@/store/authStore";
import {
  useStoreManagementStore,
  useRealtimeStoreManagement,
} from "@/store/storeManagement";
import { useToast } from "@/hooks/use-toast";
import { StaffList } from "./StaffList";
import { InviteStaffDialog } from "./InviteStaffDialog";
import { StaffManagementHeader } from "./StaffManagementHeader";
import { InvitationsList } from "../stores/employees/InvitationsList";
import { EmployeeActivitiesTab } from "./EmployeeActivitiesTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, UserPlus, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/use-analytics";

export function StaffManagementContainer() {
  const { user } = useAuthStore();
  const { employees, fetchEmployees, loading, invitations, fetchStores } =
    useStoreManagementStore();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("staff");
  const [pendingInvitations, setPendingInvitations] = useState([]);

  const { selectedStoreId } = useStoreManagementStore();
  const { t } = useTranslation();
  const { trackFeatureUsage } = useAnalytics();

  // Setup realtime subscriptions
  useRealtimeStoreManagement();

  // Filter invitations to only show pending ones
  useEffect(() => {
    if (invitations) {
      const filtered = invitations.filter((inv) => !inv.accepted);
      setPendingInvitations(filtered);
    }
  }, [invitations]);

  // Fetch stores and employees only when necessary
  useEffect(() => {
    if (user?.id) {
      fetchStores();
      fetchEmployees(selectedStoreId);
    }
  }, [user?.id, selectedStoreId]);

  // Only Manager can see all staff members
  const isManager = user?.role === "manager" || user?.role === "super_admin";

  return (
    <div className="space-y-6">
      <StaffManagementHeader
        isManager={isManager}
        onInviteClick={() => setShowInviteDialog(true)}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="staff" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span>{t("staffMembers")}</span>
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            <span>
              {t("pendingInvitations")}{" "}
              {pendingInvitations.length > 0 &&
                `(${pendingInvitations.length})`}
            </span>
          </TabsTrigger>
          <TabsTrigger value="activities" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <span>Employee Activities</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="staff">
          <StaffList
            staff={employees}
            isLoading={loading}
            isAdminHQ={isManager}
          />
        </TabsContent>

        <TabsContent value="invitations">
          <InvitationsList invitations={pendingInvitations} loading={loading} />
        </TabsContent>

        <TabsContent value="activities">
          <EmployeeActivitiesTab
            employees={employees}
            fetchEmployees={fetchEmployees}
            storeId={selectedStoreId}
          />
        </TabsContent>
      </Tabs>

      {showInviteDialog && (
        <InviteStaffDialog
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          selectedStoreId={selectedStoreId}
        />
      )}
    </div>
  );
}
